import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useSalaryCodeMutations } from './hooks'
import type {
  AllSalaryCodesQuery,
  AllSalaryCodesQueryVariables,
  SalaryCodeNode,
} from '@/modules/companies/types.graphql'
import { useTranslate } from '@ur-solutions/react-hooks'
import { OldAddButton, Header, OldSection, SectionHeader } from '../common'
import { ALL_SALARY_CODES_QUERY } from '@/modules/companies/queries'
import { useQuery } from '@apollo/client'
import { useConfirm } from '@/utils/hooks'
import { Table } from '@/components_deprecated'
import { SalaryCodePrompt } from './SalaryCodePrompt'

export interface SalaryCodeForm {
  externalId: string
  name: string
  useForAutoDietGeneration: boolean
  isDiet: boolean
  isSalaryDay: boolean
  rate: number
  showInManualTimeEntryOvertime: boolean
  showInManualTimeEntry: boolean
}

export const SalaryCodeSettings: React.JSX.CFC = () => {
  const translations = useTranslate({
    prompt: {
      delete: ['settings.prompts.delete', { name: '' }],
      deleteTitle: 'settings.prompts.salary-code-delete-title',
    },
    salaryCodes: 'common.salary-codes',
    salaryCode: 'settings.salary-type',
    name: 'common.name',
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const mutations = useSalaryCodeMutations()

  const { data } = useQuery<AllSalaryCodesQuery, AllSalaryCodesQueryVariables>(
    ALL_SALARY_CODES_QUERY
  )

  const salaryCodes = data?.allSalaryCodes.edges?.map(edge => edge.node) ?? []

  async function handleCreateSalaryCode() {
    const { data } = await addPrompt<SalaryCodeForm | null>(resolve => (
      <SalaryCodePrompt existingSalaryCodes={salaryCodes} onSubmit={resolve} />
    ))
    if (data === null) return

    void mutations.createSalaryCode({
      variables: {
        input: {
          ...data,
          showInTimeEntries: data.showInManualTimeEntry,
        },
      },
    })
  }

  async function handlePatchSalaryCode(salaryCode: SalaryCodeNode) {
    const { data } = await addPrompt<SalaryCodeForm | null>(resolve => (
      <SalaryCodePrompt
        initialData={{
          ...salaryCode,
          showInManualTimeEntry: salaryCode.showInTimeEntries,
        }}
        existingSalaryCodes={salaryCodes}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void mutations.patchSalaryCode({
      variables: {
        id: salaryCode.id,
        input: {
          externalId: data.externalId,
          name: data.name,
          isDiet: data.isDiet,
          useForAutoDietGeneration: data.useForAutoDietGeneration,
          isSalaryDay: data.isSalaryDay,
          rate: data.rate,
          showInManualTimeEntryOvertime: data.showInManualTimeEntryOvertime,
          showInTimeEntries: data.showInManualTimeEntry,
        },
      },
    })
  }

  async function handleDeleteSalaryCode(salaryCode: SalaryCodeNode) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: salaryCode.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void mutations.deleteSalaryCode({
      variables: {
        id: salaryCode.id,
      },
    })
  }

  return (
    <OldSection>
      <Header>
        <SectionHeader>{translations.salaryCodes}</SectionHeader>
        <OldAddButton adding={false} onClick={handleCreateSalaryCode} />
      </Header>

      <Table
        noShadow
        noBorders
        noHeaderMargin
        noRowMargin
        rowHoverColor="secondary"
      >
        <thead>
          <tr>
            <th>{translations.name}</th>
            <th colSpan={2}>{translations.salaryCode}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {salaryCodes.map(salaryCode => (
            <tr key={salaryCode.id}>
              <td>{salaryCode.name}</td>

              <td>{salaryCode.externalId}</td>

              <td width="1px">
                <Icon
                  icon="edit"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="secondary"
                  onClick={() => handlePatchSalaryCode(salaryCode)}
                />
              </td>

              <td width="1px">
                <Icon
                  icon="times"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="red"
                  onClick={() => handleDeleteSalaryCode(salaryCode)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </OldSection>
  )
}
